import styled from 'styled-components';
import Plate, { Row, RowLabel, RowValue, Label, Divider, Dot } from './Plate';
import Reticle from './Reticle';
import { COLORS, display, monoValue } from '../../lib/theme';
import { formatAgeMinSec, ageColor } from '../../lib/units';
import { linkColor, LINK_SOURCE } from '../../lib/link';

const BLINK_WITHIN_MS = 10 * 60_000;

// Asset number of the unit this HUD is bound to.
const ASSET = 'CC-262';

const Layer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 10;
`;

// The one plate over the portrait feed: who this is, and whether it is
// talking. Identity on top, link rows beneath a hairline.
const Identity = styled(Plate)`
  top: 16px;
  left: 16px;
  min-width: 156px;
`;

const IdentityTitle = styled.div`
  ${display}
  font-size: 17px;
  color: ${COLORS.text};
  line-height: 1.15;
`;

const Asset = styled(Label)`
  color: ${COLORS.accent};
`;

const Brand = styled(Plate)`
  left: 16px;
  bottom: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

// Never recoloured, redrawn or placed on yellow; clear space kept at or above
// the wordmark's own cap height.
const Wordmark = styled.img`
  height: 15px;
  width: auto;
  display: block;
`;

const StatusRow = styled(Row)`
  gap: 8px;
  align-items: center;
`;

const StatusText = styled.span`
  ${monoValue}
  color: ${COLORS.text};
`;

export default function Cam0Overlay({
  platformName,
  latestReceivedAt,
  latestTimeSource,
  lastHeard,
  nowMs,
  linkState,
}) {
  const ageMs = latestReceivedAt ? nowMs - latestReceivedAt.getTime() : null;
  // How long the controller's own link has been quiet -- shown only while
  // the Pi is the one keeping the data or the link alive, when that is the
  // question.
  const denshiAgeMs = lastHeard?.denshi ? nowMs - lastHeard.denshi.getTime() : null;
  const utc = latestReceivedAt ? latestReceivedAt.toISOString().slice(11, 19) : '--';

  return (
    <Layer>
      <Reticle />

      <Identity $priority>
        <IdentityTitle>{platformName}</IdentityTitle>
        <Row>
          <Asset>{ASSET}</Asset>
          <Label>Remote buoy feed</Label>
        </Row>

        <Divider />

        <Row>
          {/* RX rather than UTC when all we have is when the message arrived
              here -- an unsynced device clock must not pass for a wall clock. */}
          <RowLabel>{latestTimeSource === 'arrival' ? 'RX' : 'UTC'}</RowLabel>
          <RowValue>{utc}</RowValue>
        </Row>
        <Row>
          <RowLabel>AGE</RowLabel>
          <RowValue $color={ageColor(ageMs)}>
            {ageMs != null ? formatAgeMinSec(ageMs) : '--'}
          </RowValue>
        </Row>
        <StatusRow>
          <RowLabel>LIVE</RowLabel>
          {/* Blinks while data is fresh (last update within 10 minutes), goes
              solid once it isn't; colour and the source tag come from
              linkState -- see lib/link.js. */}
          <StatusRow as="span">
            <Dot $color={linkColor(linkState)} $blink={ageMs != null && ageMs <= BLINK_WITHIN_MS} />
            <RowValue $color={linkColor(linkState)}>{LINK_SOURCE[linkState]}</RowValue>
          </StatusRow>
        </StatusRow>
        {(linkState === 'bridge' || linkState === 'pi') && (
          <Row>
            <RowLabel>DENSHI AGE</RowLabel>
            <RowValue $color={ageColor(denshiAgeMs)}>
              {denshiAgeMs != null ? formatAgeMinSec(denshiAgeMs) : '--'}
            </RowValue>
          </Row>
        )}
      </Identity>

      <Brand>
        <Wordmark src="/weco-wordmark-white.png" alt="Weco" />
        <StatusText as={Label}>E-SEA Power</StatusText>
      </Brand>
    </Layer>
  );
}
